import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import ApiService from "../../services/Api.service";

const OPERATORS = [">", ">=", "<", "<=", "="];

const CampaignCallbackModel = ({ item, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    campaign_id: item?.id || "",
    disposition: [],
    status: [],
    call_count: 1,
    operator: ">=",
  });

  const [dispositionOptions, setDispositionOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [callCountError, setCallCountError] = useState("");
  const [leadCount, setLeadCount] = useState(null);
  const [canProceed, setCanProceed] = useState(false);

  /* ---------- Fetch Options ---------- */
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const disRes = await ApiService.getDispositionOptions();
        const statusRes = await ApiService.getCallStatusOptions();

        setDispositionOptions(disRes?.data?.data || []);
        setStatusOptions(statusRes?.data?.data || []);
      } catch (err) {
        console.error("Error loading options:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  /* ---------- Checkbox Handler ---------- */
  const handleCheckboxChange = (field, value) => {
    setFormData((prev) => {
      const exists = prev[field].includes(value);
      return {
        ...prev,
        [field]: exists
          ? prev[field].filter((v) => v !== value)
          : [...prev[field], value],
      };
    });

    setLeadCount(null);
    setCanProceed(false);
  };

  useEffect(() => {
    // Do not call API if nothing is selected
    if (!formData.disposition.length && !formData.status.length) {
      setLeadCount(null);
      setCanProceed(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoadingCount(true);
      setLeadCount(null);
      setCanProceed(false);

      try {
        const payload = {
          campaign_id: formData.campaign_id,
          disposition: formData.disposition,
          status: formData.status,
          call_count: Number(formData.call_count),
          operator: formData.operator,
        };

        const res = await ApiService.getCallbackLeadCount(payload);

        if (res.status === 200) {
          const count = res.data?.count || 0;
          setLeadCount(count);
          setCanProceed(count > 0);
        }
      } catch (error) {
        console.error("Error fetching lead count", error);
      } finally {
        setLoadingCount(false);
      }
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [
    formData.disposition,
    formData.status,
    formData.call_count,
    formData.operator,
  ]);


  /* ---------- Save (Re-Churn) ---------- */
  const handleSave = async () => {
    if (!canProceed) return;

    try {
      const payload = {
        campaign_id: formData.campaign_id,
        disposition: formData.disposition,
        status: formData.status,
        call_count: Number(formData.call_count),
        operator: formData.operator,
      };

      const res = await ApiService.updateLeadCallback(payload);

      if (res.status === 200) {
        onSave();   // refresh parent / success callback
        onClose();  // close modal
      }
    } catch (error) {
      console.error("Error saving callback", error);
    }
  };


  return (
    <div className="add-subparam-overlay">
      <div className="add-subparam-modal campaign-modal">
        {/* Header */}
        <div className="add-subparam-header">
          <h3>Lead Call Back Filters</h3>
          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="callback-body">
          <div className="parent-filter-section">
            {/* Disposition */}
            <div className="filter-section">
              <h4>Disposition</h4>
              {dispositionOptions.map((d) => (
                <label key={d.value} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.disposition.includes(d.value)}
                    onChange={() =>
                      handleCheckboxChange("disposition", d.value)
                    }
                  />
                  {d.label}
                </label>
              ))}
            </div>

            {/* Status */}
            <div className="filter-section">
              <h4>Status</h4>
              {statusOptions.map((s) => (
                <label key={s.value} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.status.includes(s.value)}
                    onChange={() =>
                      handleCheckboxChange("status", s.value)
                    }
                  />
                  {s.label}
                </label>
              ))}
            </div>

            {/* Call Count */}
            <div className="filter-section">
              <h4>Call Count</h4>
              <div className="call-count-row">
                <select
                  value={formData.operator}
                  onChange={(e) =>
                    setFormData({ ...formData, operator: e.target.value })
                  }
                >
                  {OPERATORS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  min={1}
                  value={formData.call_count}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "") {
                      setFormData({ ...formData, call_count: "" });
                      setCallCountError("Please enter a number");
                    } else if (Number(value) < 1) {
                      setFormData({ ...formData, call_count: value });
                      setCallCountError("Value must be at least 1");
                    } else {
                      setFormData({ ...formData, call_count: Number(value) });
                      setCallCountError("");
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "" || Number(e.target.value) < 1) {
                      setFormData({ ...formData, call_count: 1 }); // fallback
                      setCallCountError("");
                    }
                  }}
                />


              </div>
              {callCountError && (
                <p style={{ color: "red" }}>{callCountError}</p>
              )}
            </div>
            {/* Actions */}
            {loadingCount && (
              <div className="filter-section">
                <p>Checking matching leads...</p>
              </div>
            )}

            {/* Matching Leads */}
            {leadCount !== null && (
              <div className="filter-section">
                <h4>Matching Leads</h4>
                <p className="lead-count-text">
                  Count: <strong>{leadCount}</strong>
                </p>
              </div>
            )}
          </div>



        </div>

        {/* Footer */}
        <div className="add-subparam-footer">
          <button className="popup-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="popup-add-btn"
            onClick={handleSave}
            disabled={!canProceed}
          >
            Re-Churn
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignCallbackModel;
